// 生产安全（/safety-flow）
// 酪蛋白产业链数字化页「生产安全」的下级页面
// 由 2223 版本移植：2221 舞台为 7680×2160（2223 为 3840×2160），
// 故横向尺寸与字号按 ×2 放大、纵向在 2160 预算内重排，保证整页一屏看完不滚动
<template>
  <div class="sf-page" :style="rootVars">
    <div class="sf-viewport" ref="viewport">
      <div class="sf-scale-box" ref="box">
        <div class="sf-stage" ref="stage">
          <!-- 页头标题条 -->
          <header class="sf-head">
            <div class="sf-banner">
              <i class="sf-banner-dot"></i>
              <span class="sf-banner-text">{{ $C('safetyFlow','bannerText','酪蛋白生产安全流程') }}</span>
            </div>
            <div class="sf-banner-line"></div>
            <button class="sf-back" type="button" @click="goBack">
              <span class="sf-back-arrow">←</span> {{ fromChain ? $C('safetyFlow','backFromChain','返回产业链') : $C('safetyFlow','backDefault','返回') }}
            </button>
          </header>

          <!-- 设备流程面板 -->
          <section class="sf-panel">
            <div class="sf-flow">
              <template v-for="(d, i) in devices">
                <div class="sf-node" :key="d.key" @click="openDevice(d)">
                  <div class="sf-podium">
                    <span class="sf-glow"></span>
                    <span class="sf-plate"></span>
                    <span class="sf-icon"><FlowIcon :name="d.icon" /></span>
                  </div>
                  <p class="sf-label">{{ d.name }}</p>
                </div>
                <svg v-if="i < devices.length - 1" class="sf-arrow" :key="d.key + '-a'" width="88" height="36" viewBox="0 0 44 18" fill="none">
                  <path d="M2 9h32" stroke="#4fd8ff" stroke-width="3" stroke-linecap="round" />
                  <path d="M32 2.5L42 9l-10 6.5z" fill="#4fd8ff" />
                </svg>
              </template>
            </div>
            <p class="sf-tip">{{ tip }}</p>
          </section>

          <!-- 底部统计条 -->
          <section class="sf-stats">
            <div class="sf-stat" v-for="s in stats" :key="s.label">
              <p class="sf-stat-num" :style="{ color: s.color }">{{ s.value }}<i>{{ s.unit }}</i></p>
              <p class="sf-stat-label">{{ s.label }}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FlowIcon from '@/components/ganzi/FlowIcon.vue';
import { SAFETY_DEVICES, SAFETY_TIP } from '@/data/productionSafety';
import { mediaUrl } from '@/cms/config';

// 节点图标映射（设备 → 图标语义）
const ICON_MAP = {
  crusher: 'snow', reactor: 'molecule', clarifier: 'rings', skim: 'waves',
  sterilizer: 'box', curd: 'hex', breaker: 'ringbreak', dryer: 'honeycomb',
  sifter: 'grid', packer: 'scatter',
};

// 底部统计条（与页面原硬编码值一致，后台未配置时回落此默认）
const SAFETY_STATS = [
  { label: '核心设备', value: 10, unit: '台', color: '#31FFFF' },
  { label: '今日开机', value: 10, unit: '台', color: '#31E58B' },
  { label: '安全运行', value: 126, unit: '天', color: '#FFC857' },
  { label: '今日批次', value: 12, unit: '批', color: '#eaf3ff' },
  { label: '报警事件', value: 0, unit: '起', color: '#FF7A6B' },
];

export default {
  name: 'safetyFlow',
  components: { FlowIcon },
  data() {
    return {
      scale: 1,
    };
  },
  computed: {
    /* ---- 数据层接入 CMS：后台「生产安全 · 设备总览」可直接维护，未配置时用内置默认 ---- */
    devices() {
      return this.$CList('safetyFlow', 'devices', SAFETY_DEVICES)
        .map(d => ({ ...d, icon: ICON_MAP[d.key] || 'molecule' }))
    },
    tip() { return this.$C('safetyFlow', 'tip', SAFETY_TIP) },
    stats() { return this.$CList('safetyFlow', 'stats', SAFETY_STATS) },
    // 页面背景图（后台可配；未配时为内置深色科技渐变）
    rootVars() {
      const bg = this.$C('safetyFlow', 'pageBg', '')
      const grad = 'radial-gradient(1200px 600px at 50% 110%, rgba(20, 70, 150, 0.35), rgba(0, 0, 0, 0) 70%), radial-gradient(900px 500px at 50% -10%, rgba(15, 60, 130, 0.4), rgba(0, 0, 0, 0) 70%), linear-gradient(165deg, #030b1e 0%, #061634 55%, #030b1e 100%)'
      return { '--page-bg': bg ? `url(${mediaUrl(bg)}) center/cover no-repeat #030b1e` : grad }
    },
    fromChain() {
      const f = this.$route.query && this.$route.query.from;
      return f === 'industry-chain';
    },
  },
  mounted() {
    this.$nextTick(() => this.calc());
    window.addEventListener('resize', this.calc);
  },
  activated() {
    this.$nextTick(() => this.calc());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calc);
  },
  methods: {
    goBack() {
      if (this.fromChain) this.$router.replace('/industry-chain');
      else if (window.history.length > 1) this.$router.back();
      else this.$router.replace('/industry-chain');
    },
    openDevice(d) {
      this.$router.push({ path: '/safety-device/' + encodeURIComponent(d.name), query: { from: 'safety-flow' } });
    },
    // 7680×2160 舞台等比"包含"缩放，整页一次看完不滚动
    calc() {
      const vp = this.$refs.viewport;
      const stage = this.$refs.stage;
      const box = this.$refs.box;
      if (!vp || !stage || !box) return;
      const s = Math.min(vp.clientWidth / 7680, vp.clientHeight / 2160);
      this.scale = s;
      stage.style.transform = `scale(${s})`;
      box.style.width = 7680 * s + 'px';
      box.style.height = 2160 * s + 'px';
    },
  },
};
</script>

<style lang="less" scoped>
.sf-page {
  position: fixed;
  inset: 0;
  z-index: 1000; /* 覆盖大屏导航条，页面自带返回键（与 downstream-heat / company-detail 同架构） */
  background: var(--page-bg);
  overflow: hidden;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
}
.sf-viewport {
  position: absolute;
  inset: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sf-scale-box { position: relative; }
.sf-stage {
  width: 7680px;
  height: 2160px;
  transform-origin: top left;
  padding: 110px 150px 90px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 50px;
}

/* ---------- 页头标题条 ---------- */
.sf-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 70px;
}
.sf-banner {
  flex: none;
  display: flex;
  align-items: center;
  gap: 34px;
  padding: 40px 130px 40px 80px;
  background: linear-gradient(90deg, rgba(20, 90, 190, 0.9) 0%, rgba(30, 120, 220, 0.55) 70%, rgba(30, 120, 220, 0) 100%);
  clip-path: polygon(0 0, 100% 0, calc(100% - 80px) 100%, 0 100%);
  border-top: 5px solid rgba(120, 220, 255, 0.9);
}
.sf-banner-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #7de9ff;
  box-shadow: 0 0 28px rgba(120, 230, 255, 0.95);
}
.sf-banner-text {
  font-size: 88px;
  font-weight: 700;
  letter-spacing: 16px;
  color: #fff;
  text-shadow: 0 0 36px rgba(80, 200, 255, 0.8);
  white-space: nowrap;
}
.sf-banner-line {
  flex: 1;
  height: 5px;
  margin-top: 18px;
  background: linear-gradient(90deg, rgba(90, 190, 255, 0.8), rgba(90, 190, 255, 0.05));
}
.sf-back {
  flex: none;
  background: rgba(0, 229, 255, 0.12);
  border: 2px solid rgba(0, 229, 255, 0.55);
  color: #00E5FF;
  font-size: 56px;
  letter-spacing: 6px;
  padding: 26px 68px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: rgba(0, 229, 255, 0.25);
    box-shadow: 0 0 36px rgba(0, 229, 255, 0.5);
  }
}
.sf-back-arrow { margin-right: 12px; }

/* ---------- 设备流程面板 ---------- */
.sf-panel {
  flex: 1;
  min-height: 0;
  border: 4px solid rgba(70, 140, 230, 0.45);
  border-radius: 52px;
  background: linear-gradient(180deg, rgba(16, 48, 104, 0.42) 0%, rgba(8, 26, 62, 0.55) 100%);
  box-shadow: inset 0 0 120px rgba(30, 100, 200, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 70px;
  padding: 90px 80px 60px;
}
.sf-flow {
  display: flex;
  align-items: center;
  justify-content: center;
}
.sf-node {
  width: 560px;
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  user-select: none;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-18px); }
  &:hover .sf-icon { transform: translateX(-50%) scale(1.16); }
  &:hover .sf-label { color: #aef2ff; text-shadow: 0 0 32px rgba(90, 220, 255, 0.95); }
}
.sf-podium {
  position: relative;
  width: 380px;
  height: 340px;
}
.sf-glow {
  position: absolute;
  left: 50%;
  bottom: -28px;
  width: 420px;
  height: 180px;
  transform: translateX(-50%);
  background: radial-gradient(closest-side, rgba(40, 190, 255, 0.55), rgba(40, 190, 255, 0) 72%);
}
.sf-plate {
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 336px;
  height: 116px;
  transform: translateX(-50%);
  border-radius: 50%;
  background:
    radial-gradient(ellipse at 50% 30%, rgba(120, 230, 255, 0.5) 0%, rgba(30, 110, 210, 0.35) 45%, rgba(10, 40, 100, 0.65) 100%);
  border: 4px solid rgba(140, 235, 255, 0.85);
  box-shadow:
    0 12px 48px rgba(40, 170, 255, 0.45),
    inset 0 4px 20px rgba(160, 240, 255, 0.5);
}
.sf-plate::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 236px;
  height: 76px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid rgba(170, 240, 255, 0.55);
  background: radial-gradient(ellipse at 50% 40%, rgba(80, 190, 255, 0.5), rgba(20, 70, 150, 0.25) 80%);
}
.sf-icon {
  position: absolute;
  left: 50%;
  bottom: 52px;
  width: 192px;
  height: 192px;
  transform: translateX(-50%);
  transition: transform 0.2s ease;
  filter: drop-shadow(0 0 28px rgba(80, 220, 255, 0.9));
}
.sf-label {
  margin-top: 52px;
  font-size: 66px;
  font-weight: 600;
  letter-spacing: 8px;
  color: #eaf6ff;
  white-space: nowrap;
  text-shadow: 0 0 20px rgba(70, 180, 255, 0.6);
  transition: all 0.2s ease;
}
.sf-arrow { flex: none; margin: 0 8px 110px; }
.sf-tip {
  font-size: 52px;
  letter-spacing: 10px;
  color: rgba(160, 210, 255, 0.75);
}

/* ---------- 底部统计条 ---------- */
.sf-stats {
  flex: none;
  height: 340px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 200px;
  border: 2px solid rgba(70, 140, 230, 0.35);
  border-radius: 44px;
  background: linear-gradient(180deg, rgba(14, 42, 92, 0.4), rgba(8, 24, 58, 0.5));
}
.sf-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}
.sf-stat-num {
  font-size: 144px;
  font-weight: 700;
  font-family: 'DIN Alternate', 'Arial', sans-serif;
  line-height: 1;
  i { font-style: normal; font-size: 60px; margin-left: 12px; color: rgba(200, 230, 255, 0.8); }
}
.sf-stat-label {
  font-size: 56px;
  letter-spacing: 8px;
  color: rgba(170, 210, 250, 0.85);
}
</style>
